// src/components/CintaDeLuz.tsx
//
// Fondo del hero: dos cintas de hilos de luz, una sube por cada costado (ver
// CINTAS), y responden a quien las mira.
//
// Inspirado en un reel de textura.eu que pasó Esteban (oct-2026), y sobre todo
// en la idea del texto del reel: una web que "te mira de vuelta". Por eso:
//   - el centro de la cinta se dobla hacia el cursor (en escritorio),
//   - con el scroll la cinta sube y se abre,
//   - pulsos de luz la recorren a ritmo parejo.
// Todo suavizado: la cinta persigue al cursor, no salta detrás de él.
//
// Colores de marca (cielo → cian → esmeralda), no el lima del reel.
//
// RENDIMIENTO — la primera versión iba trabada. Medido con Chrome a 1920×1080,
// comparando contra el hero anterior (ondas en canvas, 60 cuadros/s):
//   - LO QUE MÁS PESABA: líneas de más de 1 px. El navegador dibuja las finas
//     por un camino rápido y las anchas como polígonos. Con hilos de hasta
//     1,9 px y un halo de 7, la cinta sola iba a 35 cuadros/s; con todo de
//     1 px, a 60. Ver el comentario de dibujar().
//   - No pesaban: la mezcla aditiva ni el degradé (probados y descartados).
//   - Además: los hilos se agrupan por brillo en CUBETAS, un trazo por cubeta;
//     resolución tope 1×; menos hilos en el celular; sin arrays por punto.
// Además se pausa con la pestaña oculta o con el hero fuera de pantalla, y con
// prefers-reduced-motion dibuja un solo cuadro quieto.

import { useEffect, useRef } from 'react';

interface Props {
  className?: string;
}

// Paleta de marca a lo largo de la cinta, de izquierda a derecha.
const PARADAS: Array<[number, string]> = [
  [0, 'rgb(56,189,248)'],    // sky-400
  [0.45, 'rgb(34,211,238)'], // cyan-400
  [1, 'rgb(52,211,153)'],    // emerald-400
];

// Niveles de brillo: los hilos del mismo nivel se dibujan en un solo trazo.
const CUBETAS = 5;

// DOS CINTAS (oct-2026, pedido de Esteban): la original entra por abajo a la
// izquierda y sube por el costado DERECHO; la segunda es su espejo, sube por
// el IZQUIERDO. Juntas enmarcan el texto sin cruzarlo, y abajo se trenzan.
// La segunda es más tenue y con menos hilos (para que no compita y para el
// rendimiento), y su compás de pulsos es otro: así no laten a la par.
interface Cinta {
  lado: 1 | -1;       // 1: de izquierda a derecha; -1: espejada
  proporcion: number; // fracción de los hilos de la original
  brillo: number;     // multiplica la transparencia de todo
  compas: number;     // cada cuánto sale un pulso en su hilo, en segundos
  pulsos: number;
  fase: number;       // corre la ondulación para que no sea un espejo exacto
}
const CINTAS: Cinta[] = [
  { lado: 1, proporcion: 1, brillo: 1, compas: 2.4, pulsos: 7, fase: 0 },
  { lado: -1, proporcion: 0.7, brillo: 0.75, compas: 3.1, pulsos: 5, fase: 2.1 },
];

const CintaDeLuz: React.FC<Props> = ({ className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const conCursor = window.matchMedia('(pointer: fine)').matches;

    let ancho = 0;
    let alto = 0;
    // Celular: cuánto del hero entra en la primera pantalla (sin scrollear).
    // Se mide de nuevo solo si cambia el ancho: al scrollear, la barra del
    // navegador del celular aparece y desaparece, cambia el alto de la
    // ventana, y la cinta pegaría saltos.
    let primeraPantalla = 0;
    let anchoMedido = -1;
    let hilos = 44;
    let pasos = 52;
    let degradado: CanvasGradient | null = null;

    // Lo que la cinta persigue (cursor y scroll) y dónde está ahora.
    const meta = { x: 0.5, y: 0.5, scroll: 0 };
    const ahora = { x: 0.5, y: 0.5, scroll: 0 };

    const medir = () => {
      const r = canvas.getBoundingClientRect();
      ancho = r.width;
      alto = r.height;
      if (ancho !== anchoMedido) {
        anchoMedido = ancho;
        primeraPantalla = Math.max(1, window.innerHeight - (r.top + window.scrollY));
      }
      const escala = Math.min(window.devicePixelRatio || 1, 1);
      const movil = ancho < 640;
      // Celular: 14 hilos. Abiertos de costado (ver punto()) ya se ven llenos,
      // y con 26 la cinta gruesa gastaba ~10 % más de procesador que la fina
      // de antes: con el procesador lento se perdían cuadros (oct-2026).
      hilos = movil ? 14 : 44;
      // En el celular los brazos suben casi verticales: más pasos para que
      // la curva no se vea quebrada.
      pasos = movil ? 46 : 52;
      canvas.width = Math.floor(ancho * escala);
      canvas.height = Math.floor(alto * escala);
      ctx.setTransform(escala, 0, 0, escala, 0, 0);
      degradado = ctx.createLinearGradient(0, 0, ancho, 0);
      for (const [p, c] of PARADAS) degradado.addColorStop(p, c);
    };

    // Punto de un hilo; deja el resultado en px/py para no crear arrays.
    // s: 0..1 a lo largo de la cinta; k: -0.5..0.5 dentro de ella.
    let px = 0;
    let py = 0;
    const punto = (c: Cinta, s: number, k: number, t: number, i: number) => {
      // La espejada recorre la pantalla de derecha a izquierda: sube por la
      // izquierda con la misma forma.
      px = (c.lado === 1 ? -0.08 + s * 1.16 : 1.08 - s * 1.16) * ancho;
      // NUNCA CRUZA EL TEXTO. La primera versión pasaba por el medio, detrás de
      // los botones y del velo oscuro: de la cinta solo asomaban los pulsos,
      // como rayones sobre los textos de abajo.
      //   - Escritorio: entra por abajo a la izquierda, se queda abajo y sube
      //     por el costado derecho; enmarca el bloque de texto centrado.
      //   - Celular (pantalla vertical): las cintas se cruzan apenas debajo de
      //     la PRIMERA PANTALLA y suben pegadas a los costados hasta la altura
      //     del titular: una "U" que enmarca el texto. Con la curva más abierta
      //     (una "V") el cruce caía sobre los textitos de abajo de los botones
      //     y los brazos tocaban la línea más ancha del subtítulo. Se mide
      //     contra la primera pantalla y no contra el hero, que en el celular
      //     es más alto: medida contra el hero, la cinta quedaba debajo del
      //     borde y no se veía (Esteban, oct-2026: "¿podemos subir más la cinta?").
      const vertical = alto > ancho;
      const base = vertical ? Math.min(alto, primeraPantalla || alto) : alto;
      const yInicio = vertical ? 1.1 : 1.02;
      const yFinal = vertical ? 0.07 : 0.22;
      const curva = vertical ? 5 : 2.2; // >1: se queda abajo y sube al final
      let y = base * (yInicio - (yInicio - yFinal) * Math.pow(s, curva));
      const panza = Math.sin(Math.PI * s); // 0 en las puntas, 1 en el medio
      y += (ahora.y - 0.5) * base * 0.18 * panza; // el cursor dobla el centro
      y -= ahora.scroll * alto * 0.28;             // el scroll la sube
      // Abanico: los hilos se abren en el medio y se juntan en las puntas.
      const grosor = vertical ? 0.035 + 0.05 * panza : 0.06 + 0.09 * panza;
      const apertura = base * grosor * (1 + ahora.scroll * 0.8 + (ahora.x - 0.5) * 0.5);
      y += k * apertura;
      const onda = Math.sin(s * 6.5 + t * 0.8 + i * 0.37 + c.fase);
      y += onda * (4 + 10 * Math.abs(k)) * panza;
      py = y;
      // Celular: los brazos suben casi verticales, y abrir los hilos en
      // vertical no los engrosa (quedaban dos líneas finitas). Ahí se abren
      // también de costado, con su propia ondulación (Esteban, oct-2026:
      // "más gruesa la cinta en el celular").
      if (vertical) px += k * ancho * 0.05 + onda * (2 + 4 * Math.abs(k));
    };

    // Agrega un hilo (o un tramo) al trazo en curso, sin dibujarlo todavía.
    // dy lo corre en vertical: así se arma el "cuerpo" con líneas finas.
    const agregar = (c: Cinta, k: number, t: number, i: number, desde: number, hasta: number, n: number, dy = 0) => {
      let primero = true;
      for (let j = 0; j <= n; j++) {
        const s = desde + ((hasta - desde) * j) / n;
        if (s < 0 || s > 1) continue;
        punto(c, s, k, t, i);
        if (primero) {
          ctx.moveTo(px, py + dy);
          primero = false;
        } else {
          ctx.lineTo(px, py + dy);
        }
      }
    };

    const dibujar = (ms: number) => {
      const t = ms / 1000;
      ahora.x += (meta.x - ahora.x) * 0.045;
      ahora.y += (meta.y - ahora.y) * 0.045;
      ahora.scroll += (meta.scroll - ahora.scroll) * 0.08;

      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.clearRect(0, 0, ancho, alto);
      if (!degradado) return;
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = degradado;

      // TODO DE 1 PX. El navegador dibuja las líneas finas por un camino
      // rápido; las más anchas las convierte en polígonos, y eso era lo caro:
      // con hilos de hasta 1,9 px, halo de 7 px y pulsos de 6, la cinta sola
      // bajaba a 35 cuadros/s en 1920×1080 (las ondas viejas, todas de 1 px,
      // iban a 60). El brillo sale de la transparencia, y el grosor, de repetir
      // líneas finas corridas un poco.

      ctx.lineWidth = 1;
      // Celular: los hilos van abiertos de costado (ver punto()) y cada uno
      // queda más solo; sin más brillo, la cinta gruesa se veía apagada.
      const realce = alto > ancho ? 1.7 : 1;
      for (const cinta of CINTAS) {
        const n = Math.max(8, Math.round(hilos * cinta.proporcion));

        // Hilos: un trazo por nivel de brillo. Los del centro de la cinta
        // brillan más; los de los bordes casi no se ven.
        for (let c = 0; c < CUBETAS; c++) {
          ctx.beginPath();
          for (let i = 0; i < n; i++) {
            const k = i / (n - 1) - 0.5;
            const nucleo = 1 - Math.abs(k) * 2;
            if (Math.min(CUBETAS - 1, Math.floor(nucleo * CUBETAS)) !== c) continue;
            agregar(cinta, k, t, i, 0, 1, pasos);
          }
          const nivel = (c + 0.5) / CUBETAS;
          ctx.globalAlpha = (0.06 + 0.42 * nivel * nivel) * cinta.brillo * realce;
          ctx.stroke();
        }

        // Cuerpo del centro: los hilos centrales se repiten corridos ±1,2 px.
        // Es lo que hace que se lea como una cinta de luz y no como rayas.
        ctx.beginPath();
        for (let i = 0; i < n; i += 2) {
          const k = i / (n - 1) - 0.5;
          if (1 - Math.abs(k) * 2 < 0.6) continue;
          agregar(cinta, k, t, i, 0, 1, Math.ceil(pasos / 2), 1.2);
          agregar(cinta, k, t, i, 0, 1, Math.ceil(pasos / 2), -1.2);
        }
        ctx.globalAlpha = 0.1 * cinta.brillo * realce;
        ctx.stroke();

        // Pulsos: tramos brillantes que recorren la cinta, uno detrás de otro.
        // Tres líneas finas pegadas en vez de una gruesa: con la mezcla
        // aditiva, donde se tocan se suman y el centro queda más brillante.
        ctx.beginPath();
        for (let p = 0; p < cinta.pulsos; p++) {
          const i = Math.floor(((p + 0.5) / cinta.pulsos) * n);
          const k = i / (n - 1) - 0.5;
          const inicio = ((t / cinta.compas + p / cinta.pulsos) % 1) * 1.3 - 0.15;
          agregar(cinta, k, t, i, inicio, inicio + 0.09, 10, -1);
          agregar(cinta, k, t, i, inicio, inicio + 0.09, 10, 0);
          agregar(cinta, k, t, i, inicio, inicio + 0.09, 10, 1);
        }
        ctx.globalAlpha = 0.55 * cinta.brillo * realce;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    };

    let rafId = 0;
    let corriendo = false;
    const vuelta = (ts: number) => {
      if (!corriendo) return;
      dibujar(ts);
      rafId = requestAnimationFrame(vuelta);
    };
    const arrancar = () => {
      if (corriendo || reducido) return;
      corriendo = true;
      rafId = requestAnimationFrame(vuelta);
    };
    const parar = () => {
      corriendo = false;
      cancelAnimationFrame(rafId);
    };

    const alMover = (e: PointerEvent) => {
      meta.x = e.clientX / window.innerWidth;
      meta.y = e.clientY / window.innerHeight;
    };
    const alScrollear = () => {
      meta.scroll = Math.min(1, window.scrollY / Math.max(1, alto));
    };
    const alCambiarTamano = () => {
      medir();
      if (reducido) dibujar(0);
    };
    const alCambiarVisibilidad = () => (document.hidden ? parar() : arrancar());

    medir();
    if (reducido) dibujar(0);
    else arrancar();

    if (conCursor && !reducido) window.addEventListener('pointermove', alMover, { passive: true });
    if (!reducido) window.addEventListener('scroll', alScrollear, { passive: true });
    window.addEventListener('resize', alCambiarTamano);
    document.addEventListener('visibilitychange', alCambiarVisibilidad);

    let observador: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observador = new IntersectionObserver((entradas) => {
        if (entradas[0]?.isIntersecting) arrancar();
        else parar();
      });
      observador.observe(canvas);
    }

    return () => {
      parar();
      window.removeEventListener('pointermove', alMover);
      window.removeEventListener('scroll', alScrollear);
      window.removeEventListener('resize', alCambiarTamano);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      observador?.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
};

export default CintaDeLuz;
